import { createContext, use, useState, type ComponentType, type ReactNode } from "react";
import Button from "@mui/material/Button";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  gridExpandedSortedRowIdsSelector,
  GridHeaderCheckbox,
  gridRowSelectionIdsSelector,
  useGridApiContext,
  useGridSelector,
  type DataGridPremiumProps,
  type GridColDef,
  type GridColumnHeaderParams,
  type GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { Checkbox, WhiteSpace } from "@privacy/ui";
import type { GridProps } from "../Grid";
import { Drawer } from "./Drawer";

type SelectionAction = {
  label: ReactNode;
  renderDialog: (args: { selectedIds: Array<string>; onClose: () => void; onSuccess: () => void }) => ReactNode;
};

type SelectableGridProps<Row extends GridValidRowModel> = Omit<
  GridProps<Row>,
  | "slots"
  | "sx"
  | "checkboxSelection"
  | "disableRowSelectionExcludeModel"
  | "disableRowSelectionOnClick"
  | "keepNonExistentRowsSelected"
  | "isRowSelectable"
  | "rowSelectionModel"
  | "onRowSelectionModelChange"
  | "showColumnHeaders"
> & {
  isRowLocked?: (row: Row) => boolean;
  noRowsOverlay: ComponentType;
  selectionAction: SelectionAction;
};

const checkboxSelectionColumn = <Row extends GridValidRowModel>(
  isRowLocked: ((row: Row) => boolean) | undefined
): GridColDef => ({
  ...GRID_CHECKBOX_SELECTION_COL_DEF,
  renderHeader: params => <SelectableHeaderCheckbox {...params} />,
  renderCell: params =>
    isRowLocked?.(params.row) ? (
      <CustomCheckbox checked disabled />
    ) : (
      GRID_CHECKBOX_SELECTION_COL_DEF.renderCell?.(params)
    ),
});

const SelectionActionContext = createContext<SelectionAction | null>(null);

export const SelectableGrid = <Row extends GridValidRowModel>({
  selectionAction,
  noRowsOverlay,
  isRowLocked,
  columns,
  ...props
}: SelectableGridProps<Row>) => (
  <SelectionActionContext value={selectionAction}>
    <Drawer.Grid
      showColumnHeaders
      checkboxSelection
      disableRowSelectionExcludeModel
      disableRowSelectionOnClick={false}
      keepNonExistentRowsSelected
      isRowSelectable={({ row }) => !isRowLocked?.(row)}
      sx={{
        "& .MuiDataGrid-columnHeaderCheckbox": {
          transform: "translateX(-1px)",
        },
      }}
      slots={{
        toolbar: SelectableGridToolbar,
        baseCheckbox: CustomCheckbox,
        noRowsOverlay,
      }}
      columns={[checkboxSelectionColumn(isRowLocked), ...columns]}
      {...props}
    />
  </SelectionActionContext>
);

const SelectableGridToolbar = () => {
  const selectionAction = use(SelectionActionContext);
  if (!selectionAction) throw new Error("SelectableGridToolbar must render inside Drawer.SelectableGrid");

  const [dialogIds, setDialogIds] = useState<Array<string> | null>(null);
  const apiRef = useGridApiContext();
  const selectedRows = useGridSelector(apiRef, gridRowSelectionIdsSelector);
  const selectedIds = Array.from(selectedRows.keys())
    .filter(id => apiRef.current.getRow(id))
    .map(String);

  return (
    <>
      <Drawer.GridToolbar sx={{ py: 2 }}>
        <Button
          variant="text"
          size="small"
          sx={{ ml: "auto", color: theme => `${theme.palette.primary["600"]}` }}
          disabled={selectedIds.length === 0}
          onClick={() => setDialogIds(selectedIds)}
        >
          {selectionAction.label}
          <WhiteSpace />
          {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
        </Button>
      </Drawer.GridToolbar>
      {dialogIds &&
        selectionAction.renderDialog({
          selectedIds: dialogIds,
          onClose: () => setDialogIds(null),
          onSuccess: () => {
            apiRef.current.setRowSelectionModel({ type: "include", ids: new Set() });
            setDialogIds(null);
          },
        })}
    </>
  );
};

const SelectableHeaderCheckbox = (params: GridColumnHeaderParams) => {
  const apiRef = useGridApiContext();
  const visibleRowIds = useGridSelector(apiRef, gridExpandedSortedRowIdsSelector);
  if (visibleRowIds.length > 0 && !visibleRowIds.some(id => apiRef.current.isRowSelectable(id))) return null;

  return <GridHeaderCheckbox {...params} />;
};

type BaseCheckboxSlot = NonNullable<NonNullable<DataGridPremiumProps["slots"]>["baseCheckbox"]>;

const CustomCheckbox: BaseCheckboxSlot = props => {
  const { slotProps, ...checkboxProps } = props;
  return <Checkbox {...checkboxProps} size="small" indeterminate={false} slotProps={{ input: slotProps?.htmlInput }} />;
};
